import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tile,
  TextInput,
  Select,
  SelectItem,
  Dropdown,
  Button,
  InlineNotification,
  Loading,
  Modal,
} from '@carbon/react';
import {
  User,
  Password,
  TrashCan,
  Settings as SettingsIcon,
  Locked,
  Package,
  CheckmarkFilled,
  WarningFilled,
  Renew,
} from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { getAppSettings, updateAppSettings, updatePassword } from '../services/userSettingsService';
import { mergeAppSettings } from '../services/appSettingsSchema';
import NativeSettingsForm from '../components/settings/NativeSettingsForm';
import ApiKeysSettings from '../components/settings/ApiKeysSettings';
import { getMyActor, setMyActor } from '../services/shipmentService';
import './SettingsPage.scss';

const subscriptionGrantsAccess = (sub) => {
  if (!sub?.status) return false;
  const st = String(sub.status).toLowerCase();
  if (st !== 'active' && st !== 'trialing') return false;
  const end = sub.currentPeriodEnd || sub.current_period_end;
  if (end) {
    const endMs = new Date(end).getTime();
    if (!Number.isNaN(endMs) && endMs < Date.now()) return false;
  }
  return true;
};

const getSubscriptionPeriodCaption = (sub) => {
  const end = sub?.currentPeriodEnd || sub?.current_period_end;
  if (!end) return null;
  const endMs = new Date(end).getTime();
  if (Number.isNaN(endMs)) return null;
  const ds = new Date(end).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const st = String(sub.status || '').toLowerCase();
  if (subscriptionGrantsAccess(sub)) return `Renews ${ds}`;
  if (st === 'past_due' || st === 'unpaid') return `Payment required · period ended ${ds}`;
  if (st === 'canceled') return `Access until ${ds}`;
  return `Period ends ${ds}`;
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user, deleteAccount, signOut } = useAuth();
  const {
    subscription,
    loading: subscriptionLoading,
    refreshSubscription,
  } = useSubscription();
  const isDark = theme === 'dark';
  const subscriptionInGoodStanding = subscriptionGrantsAccess(subscription);
  const subscriptionPeriodCaption = getSubscriptionPeriodCaption(subscription);
  const planLabel = subscription
    ? (subscription.planName ||
      subscription.plan_name ||
      (subscription.plan
        ? `${String(subscription.plan).charAt(0).toUpperCase()}${String(subscription.plan).slice(1)} Plan`
        : 'No Plan'))
    : null;
  const sensorsIncluded = subscription?.sensorsIncluded ?? subscription?.sensors_included;
  const additionalSensors =
    subscription?.additionalSensorsQuantity ?? subscription?.additional_sensors_quantity ?? 0;
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  
  // Account Settings
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [hasPinCode, setHasPinCode] = useState(false); // Track if PIN exists in database
  
  // Native AppSettings (stored in user_profiles.alert_settings)
  const [appSettings, setAppSettings] = useState(null);
  
  // Notification states
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [clearDataConfirm, setClearDataConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Supply Chain Role
  const [actorRole, setActorRole] = useState('');
  const [actorCompany, setActorCompany] = useState('');
  const [actorGln, setActorGln] = useState('');
  const [actorLoading, setActorLoading] = useState(false);
  const [actorSaving, setActorSaving] = useState(false);
  const [actorSaved, setActorSaved] = useState(false);
  const [actorError, setActorError] = useState('');

  // Load user settings from Supabase
  useEffect(() => {
    const loadSettings = async () => {
      if (!user) return;
      
      try {
        setIsLoading(true);
        const { email: loadedEmail, is_pin_set, appSettings: loaded } = await getAppSettings();
        
        setUsername(loaded.username || '');
        setEmail(loadedEmail || '');
        setHasPinCode(is_pin_set ?? false);
        setPinCode('');
        setAppSettings(loaded);
      } catch (error) {
        console.error('Error loading settings:', error);
        setSaveError('Failed to load settings. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [user]);

  // Load supply chain actor role
  useEffect(() => {
    const loadActor = async () => {
      if (!user) return;
      setActorLoading(true);
      try {
        const actor = await getMyActor();
        if (actor) {
          setActorRole(actor.actor_type || '');
          setActorCompany(actor.company_name || '');
          setActorGln(actor.default_gln || '');
        }
      } catch (err) {
        // Table may not exist yet — silently ignore
        console.warn('SettingsPage: could not load supply chain actor', err?.message);
      } finally {
        setActorLoading(false);
      }
    };
    loadActor();
  }, [user]);

  const handleSaveActor = async () => {
    if (!actorRole) {
      setActorError('Please select a role.');
      return;
    }
    setActorSaving(true);
    setActorError('');
    try {
      await setMyActor({
        actorType: actorRole,
        companyName: actorCompany,
        defaultGln: actorGln,
      });
      setActorSaved(true);
      setTimeout(() => setActorSaved(false), 3000);
    } catch (err) {
      setActorError(err.message || 'Failed to save role. Ensure the supply_chain_actors table exists.');
    } finally {
      setActorSaving(false);
    }
  };

  const handleAppSettingsChange = (patch) => {
    setAppSettings((prev) => mergeAppSettings(prev, patch));
  };

  const handleSaveSettings = async (section) => {
    setIsSaving(true);
    setSaveError('');
    
    try {
      if (section === 'profile') {
        await updateAppSettings({ username, email });
      } else if (section === 'pin') {
        if (!pinCode || pinCode.length < 4) {
          setSaveError('PIN code must be at least 4 digits.');
          setIsSaving(false);
          return;
        }
        await updateAppSettings({ pin_code: pinCode });
        const { is_pin_set, appSettings: loaded } = await getAppSettings();
        setHasPinCode(is_pin_set ?? false);
        setPinCode('');
        setAppSettings(loaded);
      } else if (appSettings) {
        await updateAppSettings(appSettings);
        if (section === 'retention' && user) {
          const { supabase } = await import('../services/supabaseClient');
          const inverted = !appSettings.retentionEnabled;
          await supabase
            .from('gateways')
            .update({ retention_enabled: inverted, retention_days: appSettings.retentionDays || 90 })
            .eq('user_id', user.id);
          await supabase
            .from('sensors')
            .update({ retention_enabled: inverted, retention_days: appSettings.retentionDays || 90 })
            .eq('user_id', user.id);
        }
      }
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveError(error.message || 'Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      setSaveError('Password must be at least 8 characters long.');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setSaveError('New password and confirm password do not match.');
      return;
    }
    
    setIsSaving(true);
    setSaveError('');
    
    try {
      await updatePassword(newPassword);
      setSaveSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating password:', error);
      setSaveError(error.message || 'Failed to update password. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearSensorData = () => {
    if (clearDataConfirm) {
      alert('Sensor data cleared successfully - Demo mode');
      setClearDataConfirm(false);
    } else {
      setClearDataConfirm(true);
      setTimeout(() => setClearDataConfirm(false), 5000);
    }
  };

  if (isLoading) {
    return (
      <div className={`settings-page ${isDark ? 'settings-page--dark' : 'settings-page--light'}`}>
        <div className="settings-page__container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Loading description="Loading settings..." />
        </div>
      </div>
    );
  }

  return (
    <div className={`settings-page ${isDark ? 'settings-page--dark' : 'settings-page--light'}`}>
      <div className="settings-page__container">
        <div className="settings-page__header">
          <h1 className="settings-page__title">
            <SettingsIcon size={28} /> Settings
          </h1>
          <p className="settings-page__subtitle">
            Manage your account preferences and app settings
          </p>
        </div>

        {saveSuccess && (
          <InlineNotification
            kind="success"
            title="Success"
            subtitle="Settings saved successfully"
            onClose={() => setSaveSuccess(false)}
            className="settings-page__notification"
          />
        )}

        {saveError && (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={saveError}
            onClose={() => setSaveError('')}
            className="settings-page__notification"
          />
        )}

        {/* Account Section — subscription status first (matches native Settings) */}
        <h2 className="settings-page__section-title">Account</h2>
        <div className="settings-page__section">
          <Tile className="settings-page__card">
            <div className="settings-page__card-header">
              <User size={20} />
              <h3>Account & Subscription</h3>
            </div>
            <div className="settings-page__card-content">
              <div className="settings-page__account-summary">
                <div className="settings-page__account-email">{user?.email || email}</div>
                <div className="settings-page__account-status">Logged in</div>
              </div>

              {subscriptionLoading && (
                <div className="settings-page__plan-badge settings-page__plan-badge--loading">
                  <Loading small withOverlay={false} />
                  <span>Loading plan…</span>
                </div>
              )}

              {!subscriptionLoading && !subscription && (
                <div className="settings-page__plan-badge settings-page__plan-badge--inactive">
                  <WarningFilled size={14} />
                  <span>No active subscription</span>
                </div>
              )}

              {!subscriptionLoading && subscription && (
                <>
                  <div
                    className={`settings-page__plan-badge ${
                      subscriptionInGoodStanding
                        ? 'settings-page__plan-badge--active'
                        : 'settings-page__plan-badge--inactive'
                    }`}
                  >
                    {subscriptionInGoodStanding ? <CheckmarkFilled size={14} /> : <WarningFilled size={14} />}
                    <span className="settings-page__plan-badge-text">
                      {planLabel}
                      {sensorsIncluded != null && (
                        <span className="settings-page__plan-badge-sensors">
                          {' '}· {sensorsIncluded} sensor{sensorsIncluded !== 1 ? 's' : ''}
                        </span>
                      )}
                    </span>
                  </div>
                  {subscriptionPeriodCaption && (
                    <div className="settings-page__plan-period">{subscriptionPeriodCaption}</div>
                  )}
                  {typeof additionalSensors === 'number' && additionalSensors > 0 && (
                    <div
                      className={`settings-page__plan-badge ${
                        subscriptionInGoodStanding
                          ? 'settings-page__plan-badge--active'
                          : 'settings-page__plan-badge--inactive'
                      }`}
                    >
                      <span className="settings-page__plan-badge-text">
                        Additional Sensors · +{additionalSensors}
                      </span>
                    </div>
                  )}
                </>
              )}

              <div className="settings-page__account-actions">
                <Button
                  kind="primary"
                  renderIcon={Renew}
                  onClick={async () => {
                    try {
                      await refreshSubscription?.();
                    } catch (e) {
                      console.warn('Failed to refresh subscription', e);
                    }
                    navigate('/dashboard/subscription');
                  }}
                >
                  Upgrade Plan
                </Button>
                <Button
                  kind="danger"
                  onClick={async () => {
                    try {
                      await signOut();
                      navigate('/login');
                    } catch (e) {
                      setSaveError(e.message || 'Logout failed');
                    }
                  }}
                >
                  Log Out
                </Button>
              </div>
            </div>
          </Tile>

          <Tile className="settings-page__card">
            <div className="settings-page__card-header">
              <User size={20} />
              <h3>Profile Information</h3>
            </div>
            <div className="settings-page__card-content">
              <div className="settings-page__field">
                <TextInput
                  id="username"
                  labelText="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
              <div className="settings-page__field">
                <TextInput
                  id="email"
                  type="email"
                  labelText="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <Button kind="primary" onClick={() => handleSaveSettings('profile')} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>
          </Tile>

          <Tile className="settings-page__card">
            <div className="settings-page__card-header">
              <Password size={20} />
              <h3>Change Password</h3>
            </div>
            <div className="settings-page__card-content">
              <div className="settings-page__field">
                <TextInput
                  id="currentPassword"
                  type="password"
                  labelText="Current Password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>
              <div className="settings-page__field">
                <TextInput
                  id="newPassword"
                  type="password"
                  labelText="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  helperText="Must be at least 8 characters"
                />
              </div>
              <div className="settings-page__field">
                <TextInput
                  id="confirmPassword"
                  type="password"
                  labelText="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              <Button kind="primary" onClick={handleUpdatePassword} disabled={isSaving}>
                {isSaving ? 'Updating...' : 'Update Password'}
              </Button>
            </div>
          </Tile>

          <Tile className="settings-page__card">
            <div className="settings-page__card-header">
              <Locked size={20} />
              <h3>PIN Code</h3>
            </div>
            <div className="settings-page__card-content">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <span style={{ fontSize: '0.875rem', color: isDark ? '#c6c6c6' : '#525252' }}>Status:</span>
                {hasPinCode ? (
                  <span style={{ 
                    padding: '4px 12px', 
                    borderRadius: '4px', 
                    fontSize: '0.75rem', 
                    fontWeight: 500,
                    backgroundColor: '#24a14820',
                    color: '#24a148'
                  }}>
                    Active
                  </span>
                ) : (
                  <span style={{ 
                    padding: '4px 12px', 
                    borderRadius: '4px', 
                    fontSize: '0.75rem', 
                    fontWeight: 500,
                    backgroundColor: '#da1e2820',
                    color: '#da1e28'
                  }}>
                    Disabled
                  </span>
                )}
              </div>
              <p className="settings-page__description">
                {hasPinCode 
                  ? 'Enter a new PIN code to update your existing PIN, or click Reset PIN to disable it.'
                  : 'Set a 4-6 digit PIN code for quick access to the app'
                }
              </p>
              <div className="settings-page__field">
                <TextInput
                  id="pinCode"
                  type="password"
                  labelText={hasPinCode ? "New PIN Code (leave empty to keep current)" : "PIN Code"}
                  value={pinCode}
                  onChange={(e) => setPinCode(e.target.value)}
                  maxLength={6}
                  placeholder={hasPinCode ? "Enter new PIN (4-6 digits)" : "Enter 4-6 digits"}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <Button 
                  kind="primary" 
                  onClick={() => handleSaveSettings('pin')} 
                  disabled={isSaving || (!pinCode && !hasPinCode)}
                >
                  {isSaving ? 'Saving...' : hasPinCode ? 'Update PIN Code' : 'Set PIN Code'}
                </Button>
                {hasPinCode && (
                  <Button 
                    kind="danger" 
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to reset your PIN code? This will disable PIN protection.')) {
                        setIsSaving(true);
                        setSaveError('');
                        try {
                          await updateUserSettings({ pin_code: '' }); // Empty string will clear PIN
                          // Reload settings to get updated is_pin_set status
                          const updatedSettings = await getUserSettings();
                          setHasPinCode(updatedSettings.is_pin_set ?? false);
                          setPinCode('');
                          setSaveSuccess(true);
                          setTimeout(() => setSaveSuccess(false), 3000);
                        } catch (error) {
                          console.error('Error resetting PIN:', error);
                          setSaveError(error.message || 'Failed to reset PIN. Please try again.');
                        } finally {
                          setIsSaving(false);
                        }
                      }
                    }} 
                    disabled={isSaving}
                  >
                    {isSaving ? 'Resetting...' : 'Reset PIN'}
                  </Button>
                )}
              </div>
            </div>
          </Tile>
        </div>

        {/* Supply Chain Role Section */}
        <h2 className="settings-page__section-title">Supply Chain Role</h2>
        <div className="settings-page__section">
          <Tile className="settings-page__card">
            <div className="settings-page__card-header">
              <Package size={20} />
              <h3>EPCIS / Shipment Role</h3>
            </div>
            <div className="settings-page__card-content">
              <p className="settings-page__description">
                Set your role in the cold-chain supply chain. This determines how you interact with shipments and custody handoffs.
              </p>

              {actorSaved && (
                <InlineNotification
                  kind="success"
                  title="Saved"
                  subtitle="Your supply chain role has been updated."
                  onClose={() => setActorSaved(false)}
                  style={{ marginBottom: '16px' }}
                  lowContrast
                />
              )}
              {actorError && (
                <InlineNotification
                  kind="error"
                  title="Error"
                  subtitle={actorError}
                  onClose={() => setActorError('')}
                  style={{ marginBottom: '16px' }}
                  lowContrast
                />
              )}

              {actorLoading ? (
                <Loading small withOverlay={false} description="Loading role..." />
              ) : (
                <>
                  <div className="settings-page__field">
                    <Dropdown
                      id="actorRole"
                      titleText="Your Role"
                      label="— Select a role —"
                      items={[
                        { id: 'producer', label: 'Producer / Packer' },
                        { id: 'carrier', label: 'Carrier / Logistics' },
                        { id: 'warehouse', label: 'Warehouse / Distribution Center' },
                        { id: 'receiver', label: 'Receiver / Retailer' },
                        { id: 'admin', label: 'Admin / Multi-role' },
                      ]}
                      selectedItem={actorRole ? { id: actorRole, label: ({ producer: 'Producer / Packer', carrier: 'Carrier / Logistics', warehouse: 'Warehouse / Distribution Center', receiver: 'Receiver / Retailer', admin: 'Admin / Multi-role' })[actorRole] } : null}
                      itemToString={(item) => (item ? item.label : '')}
                      onChange={({ selectedItem }) => setActorRole(selectedItem?.id || '')}
                      helperText="Select your role in the supply chain"
                    />
                  </div>

                  <div className="settings-page__field">
                    <TextInput
                      id="actorCompany"
                      labelText="Company Name (optional)"
                      value={actorCompany}
                      onChange={(e) => setActorCompany(e.target.value)}
                      placeholder="e.g. FreshFarms Logistics"
                      helperText="Appears on EPCIS events and custody handoff records"
                    />
                  </div>

                  <div className="settings-page__field">
                    <TextInput
                      id="actorGln"
                      labelText="GS1 GLN (optional)"
                      value={actorGln}
                      onChange={(e) => setActorGln(e.target.value)}
                      placeholder="e.g. 0614141000000"
                      helperText="Your Global Location Number for EPCIS readPoint attribution"
                    />
                  </div>

                  <Button kind="primary" onClick={handleSaveActor} disabled={actorSaving || !actorRole}>
                    {actorSaving ? 'Saving...' : 'Save Role'}
                  </Button>
                </>
              )}
            </div>
          </Tile>
        </div>

        <h2 className="settings-page__section-title">Developer API</h2>
        <div className="settings-page__section">
          <ApiKeysSettings />
        </div>

        {/* Alerts — native AppSettings + web email/status */}
        <h2 className="settings-page__section-title">Alerts & Notifications</h2>
        <div className="settings-page__section">
          <NativeSettingsForm
            section="sms"
            settings={appSettings}
            onChange={handleAppSettingsChange}
            onSave={handleSaveSettings}
            isSaving={isSaving}
          />
          <NativeSettingsForm
            section="push"
            settings={appSettings}
            onChange={handleAppSettingsChange}
            onSave={handleSaveSettings}
            isSaving={isSaving}
          />
          <NativeSettingsForm
            section="webAlerts"
            settings={appSettings}
            onChange={handleAppSettingsChange}
            onSave={handleSaveSettings}
            isSaving={isSaving}
          />
        </div>

        {/* Display — native units & chart scales */}
        <h2 className="settings-page__section-title">Display Preferences</h2>
        <div className="settings-page__section">
          <NativeSettingsForm
            section="display"
            settings={appSettings}
            onChange={handleAppSettingsChange}
            onSave={handleSaveSettings}
            isSaving={isSaving}
          />
        </div>

        {/* Status trends — native zone/trend settings */}
        <h2 className="settings-page__section-title">Status Trends</h2>
        <div className="settings-page__section">
          <NativeSettingsForm
            section="trends"
            settings={appSettings}
            onChange={handleAppSettingsChange}
            onSave={handleSaveSettings}
            isSaving={isSaving}
          />
        </div>

        {/* Data — native updateFrequency, syncInterval, retention */}
        <h2 className="settings-page__section-title">Data & Sync</h2>
        <div className="settings-page__section">
          <NativeSettingsForm
            section="data"
            settings={appSettings}
            onChange={handleAppSettingsChange}
            onSave={handleSaveSettings}
            isSaving={isSaving}
          />

          <Tile className="settings-page__card settings-page__card--danger">
            <div className="settings-page__card-header">
              <TrashCan size={20} />
              <h3>Clear Sensor Data</h3>
            </div>
            <div className="settings-page__card-content">
              <p className="settings-page__description settings-page__description--warning">
                This action will permanently delete all historical sensor data. This cannot be undone.
              </p>
              {clearDataConfirm && (
                <InlineNotification
                  kind="warning"
                  title="Confirm"
                  subtitle="Click again to confirm data deletion"
                  hideCloseButton
                  className="settings-page__warning-notification"
                />
              )}
              <Button 
                kind="danger" 
                onClick={handleClearSensorData}
                renderIcon={TrashCan}
              >
                {clearDataConfirm ? 'Click to Confirm Delete' : 'Clear All Sensor Data'}
              </Button>
            </div>
          </Tile>
        </div>

        {/* Danger Zone */}
        <h2 className="settings-page__section-title" style={{ color: '#da1e28' }}>Danger Zone</h2>
        <div className="settings-page__section">
          <Tile className="settings-page__card settings-page__card--danger">
            <div className="settings-page__card-header">
              <TrashCan size={20} />
              <h3>Delete Account</h3>
            </div>
            <div className="settings-page__card-content">
              <p className="settings-page__description settings-page__description--warning">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              <p style={{ fontSize: '0.875rem', color: isDark ? '#c6c6c6' : '#525252', marginBottom: '16px' }}>
                The following will be permanently removed:
              </p>
              <ul style={{ 
                fontSize: '0.875rem', 
                color: isDark ? '#f4f4f4' : '#161616', 
                marginBottom: '16px', 
                paddingLeft: '20px',
                lineHeight: '1.8'
              }}>
                <li>Your account and login credentials</li>
                <li>All sensors, gateways and device data</li>
                <li>All sensor readings and history</li>
                <li>Orders, subscriptions and alert rules</li>
              </ul>
              <Button 
                kind="danger" 
                onClick={() => {
                  setDeleteConfirmText('');
                  setDeleteError('');
                  setDeleteModalOpen(true);
                }}
                renderIcon={TrashCan}
              >
                Delete My Account
              </Button>
            </div>
          </Tile>
        </div>

        {/* Delete Account Confirmation Modal */}
        <Modal
          open={deleteModalOpen}
          danger
          modalHeading="Delete Your Account"
          modalLabel="This action is permanent"
          primaryButtonText={isDeleting ? 'Deleting...' : 'Delete Permanently'}
          secondaryButtonText="Cancel"
          primaryButtonDisabled={deleteConfirmText.trim().toLowerCase() !== 'delete account' || isDeleting}
          onRequestClose={() => {
            if (!isDeleting) {
              setDeleteModalOpen(false);
              setDeleteConfirmText('');
              setDeleteError('');
            }
          }}
          onRequestSubmit={async () => {
            if (deleteConfirmText.trim().toLowerCase() !== 'delete account' || isDeleting) return;
            setIsDeleting(true);
            setDeleteError('');
            const { error } = await deleteAccount();
            setIsDeleting(false);
            if (error) {
              setDeleteError(error.message || 'Failed to delete account. Please try again.');
            } else {
              setDeleteModalOpen(false);
              navigate('/');
            }
          }}
        >
          <p style={{ marginBottom: '16px', color: isDark ? '#c6c6c6' : '#525252' }}>
            This action is permanent and cannot be undone. All your data will be removed across both mobile and web.
          </p>
          <p style={{ marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: isDark ? '#f4f4f4' : '#161616' }}>
            Type <strong style={{ color: '#da1e28' }}>delete account</strong> below to confirm:
          </p>
          <TextInput
            id="deleteConfirmInput"
            labelText=""
            placeholder="delete account"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            disabled={isDeleting}
            autoComplete="off"
          />
          {deleteError && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={deleteError}
              onClose={() => setDeleteError('')}
              style={{ marginTop: '16px' }}
              lowContrast
            />
          )}
        </Modal>
      </div>
    </div>
  );
};

export default SettingsPage;
